import { Box, Card, CardContent, Grid, Typography, IconButton, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material"
import { useQuery } from "react-query"
import axiosClient from "../../../../../../../lib/axios"
import { IDataRefVendasGrafico } from "../../../../../../../types/api/estoque/detalhar-referencia-vendas-grafico"
import VendasMensaisSkeleton from "./VendasMensaisSkeleton"
import { Chart } from "react-chartjs-2"
import { listaMesesAbrv } from "../../../../../../../helpers/constantes"
import SelectLoja from "../../../../../../../components/SelectsSimpleApiQuery/SelectLoja"
import { useState } from "react"
import { IDataLojas } from "../../../../../../../types/api/geral/pesquisar-lojas"
import moment from "moment"
import DateComIntervalo from "../../../../../../../components/DateComIntervalo"
import { BarChart, TableChart } from "@mui/icons-material"
import { formatarValorMonetario, formatarNumeroComSeparadorMilhar } from "../../../../../../../helpers/masks"

export default function CardUltimasVendasMensais({ produtoId }: CardUltimasVendasMensaisProps) {

    const [loja, setLoja] = useState<IDataLojas | null>(null)
    const [tipoVisualizacao, setTipoVisualizacao] = useState<'grafico' | 'tabela'>('grafico')
    const [periodo, setPeriodo] = useState<{
        data_inicio: string;
        data_fim: string;
    }>({
        data_inicio: moment().subtract(1, 'year').format('YYYY-MM-DD'),
        data_fim: moment().subtract(1, 'month').format('YYYY-MM-DD')
    })

    const { data, isLoading } = useQuery({
        queryKey: ['ultimas-vendas-mensais', produtoId, loja, periodo],
        queryFn: () => axiosClient.get(`/estoque/detalhar-referencia-vendas-grafico`, {
            params: {
                produto_id: produtoId,
                ...(loja ? { loja_id: loja.id } : {}),
                data_inicio: periodo.data_inicio,
                data_fim: periodo.data_fim
            }
        })
    })

    const vendasMensais: IDataRefVendasGrafico = data?.data || null
    const carregamento = isLoading || !vendasMensais


    const chartData = {
        labels: vendasMensais?.vendas_mensais_periodo?.map(venda => listaMesesAbrv[venda.month - 1]) || [],
        datasets: [
            {
                label: 'Vlr. vendas',
                data: vendasMensais?.vendas_mensais_periodo?.map(venda => venda.total_vlr_venda) || [],
                backgroundColor: 'rgba(255, 127, 80, 0.8)',
                borderColor: 'rgba(255, 127, 80, 1)',
                borderWidth: 1,
                type: 'bar' as const,
                yAxisID: 'y',
                order: 2,
                barPercentage: 0.6,
                categoryPercentage: 0.7
            },
            {
                label: 'Qtd. vendas',
                data: vendasMensais?.vendas_mensais_periodo?.map(venda => venda.total_qtd_venda) || [],
                backgroundColor: 'transparent',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 2,
                type: 'line' as const,
                yAxisID: 'y1',
                tension: 0,
                pointBackgroundColor: 'rgba(54, 162, 235, 1)',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointRadius: 5,
                pointHoverRadius: 7,
                order: 1
            }
        ]
    }

    const chartOptions = {
        responsive: true,
        interaction: {
            mode: 'index' as const,
            intersect: false,
        },
        plugins: {
            legend: {
                display: true,
                position: 'bottom' as const,
                labels: {
                    usePointStyle: true,
                    boxWidth: 15,
                    boxHeight: 15,
                    padding: 15,
                }
            },
            title: {
                display: true,
                text: 'Título do Gráfico',
                font: {
                    size: 14
                }
            }
        },
        scales: {
            y: {
                type: 'linear' as const,
                display: true,
                position: 'left' as const,
                title: {
                    display: true,
                    text: 'Eixo de valores'
                },
                grid: {
                    drawOnChartArea: true,
                }
            },
            y1: {
                type: 'linear' as const,
                display: true,
                position: 'right' as const,
                title: {
                    display: true,
                    text: 'Eixo de quantidades'
                },
                grid: {
                    drawOnChartArea: false,
                },
            }
        }
    }


    const handleChangeLoja = (value: IDataLojas | null) => {
        setLoja(value)
    }

    if (carregamento) {
        return <VendasMensaisSkeleton />
    }

    return (
        <Card>
            <CardContent>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" component="div">
                                Vendas mensais
                            </Typography>
                            <Box>
                                <Tooltip title="Visualizar gráfico">
                                    <IconButton
                                        onClick={() => setTipoVisualizacao('grafico')}
                                        color={tipoVisualizacao === 'grafico' ? 'primary' : 'default'}
                                        size="small"
                                    >
                                        <BarChart />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Visualizar tabela">
                                    <IconButton
                                        onClick={() => setTipoVisualizacao('tabela')}
                                        color={tipoVisualizacao === 'tabela' ? 'primary' : 'default'}
                                        size="small"
                                    >
                                        <TableChart />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <DateComIntervalo
                            data={{
                                inicial: moment(periodo.data_inicio),
                                final: moment(periodo.data_fim)
                            }}
                            onChangeData={(data) => {
                                setPeriodo({
                                    data_inicio: data.inicial?.format('YYYY-MM-DD') || '',
                                    data_fim: data.final?.format('YYYY-MM-DD') || ''
                                })
                            }}
                            datePickerView={['month', 'year']}
                            formatString="MM/YYYY"
                            labelInput="Período"
                            size="small"
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <SelectLoja
                            value={loja}
                            onChange={handleChangeLoja}
                            size="small"
                            possuiBtnLimpar
                        />
                    </Grid>
                </Grid>

                <Box mt={2} width="100%">
                    {tipoVisualizacao === 'grafico' ? (
                        vendasMensais?.vendas_mensais_periodo?.length > 0 ? (
                            <Chart
                                type="bar"
                                data={chartData}
                                options={chartOptions}
                            />
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height={300} border={1} borderColor="grey.300" borderRadius={1}>
                                <Typography variant="body2" color="textSecondary">
                                    Sem dados disponíveis para o período selecionado
                                </Typography>
                            </Box>
                        )
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Ano</strong></TableCell>
                                        <TableCell><strong>Mês</strong></TableCell>
                                        <TableCell align="right"><strong>Qtd. vendas</strong></TableCell>
                                        <TableCell align="right"><strong>Vlr. vendas</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {vendasMensais?.vendas_mensais_periodo.map((venda, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{venda.year}</TableCell>
                                            <TableCell>{listaMesesAbrv[venda.month - 1]}</TableCell>
                                            <TableCell align="right">{formatarNumeroComSeparadorMilhar(venda.total_qtd_venda)}</TableCell>
                                            <TableCell align="right">{formatarValorMonetario(venda.total_vlr_venda)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </CardContent>
        </Card>
    )
}

interface CardUltimasVendasMensaisProps {
    produtoId: number
}